import { isEmpty } from 'lodash'
import VueI18n from "vue-i18n";
import VueRouter, { NavigationGuardNext, Route } from 'vue-router'
import { Store } from "vuex";

import { checkLogin } from "../../logic/auth/logic";
import { errorNotificationWrapper } from "../../logic/helpers/NotificationService";
import PostApi from './includes/logic/Api/PostApi'
import ReturnUrl from './includes/logic/ReturnUrl'
import { i18nHelper } from "./includes/logic/i18nHelper";
import { AdminTemplateModuleStore } from "./store/StoreTypes";

//vars
const ADMIN_ROUTE_NAMES = [ 'login', 'login_admin', 'ResetPassword' ]
let loginTimeoutId: number | undefined

//helpers
const isAdminRoute = (route: Route) => route.name && ADMIN_ROUTE_NAMES.includes(route.name)
const isRequiredAuthRoute = (route: Route) => route.meta && 'requiresAuth' in route.meta ? route.meta.requiresAuth : true

//logic
const getRuntimeConfig = (store:any): Promise<void> => {
  return PostApi.sendRequest('tg', 'getruntimeconfig')
    .then((response) => {
      store.commit('AdminTemplate/SET_RUNTIME_CONFIG', response.data)

      if (response.data.license_options) {
        store.commit('commerce/TariffService/SET_TARIFFS_MAP', { tariffsMap: response.data.license_options })
      }
    })
    .catch(error => {
      errorNotificationWrapper('', error)
      console.error(error)
    })
}

const resolveProjectBeforeEachIfExists = (store:any, to: Route, from: Route, next: NavigationGuardNext) => {
  const projectGuards = store.state.AdminTemplate.applicationConfig.globalGuards

  if (typeof projectGuards?.beforeEach === 'function') {
    projectGuards.beforeEach(to, from, next)
  } else {
    next()
  }
}

const resolveProjectAfterEachIfExists = (store:any, to: Route) => {
  const projectGuards = store.state.AdminTemplate.applicationConfig.globalGuards

  if (typeof projectGuards?.afterEach === 'function') {
    projectGuards.afterEach(to)
  }
}

const onLoginCheckSuccess = async (store: any, to: Route, from: Route, next: NavigationGuardNext) => {
  // goto returnUrl if exists
  if (ReturnUrl.isExists && (isAdminRoute(from) || to.query?.loginredirect === null)) {
    ReturnUrl.go();
  }

  if (!store.state.AdminTemplate.runtime_config) {
    getRuntimeConfig(store)
      .finally(() => {
        resolveProjectBeforeEachIfExists(store, to, from, next)
      })
  } else {
    resolveProjectBeforeEachIfExists(store, to, from, next)
  }
}

export default function setRouteGuards(store: Store<{ AdminTemplate: AdminTemplateModuleStore }>, router: VueRouter, i18n?: VueI18n): any {
  router.afterEach((to) => {
    const appLoading = document.querySelector('#loading-bg')

    if (appLoading) {
      if (to.meta && to.meta.disableLoader) {
        appLoading.classList.add('hidden')
      } else {
        appLoading.classList.replace('opacity-100', 'opacity-0')

        setTimeout(() => {
          appLoading.classList.add('hidden')
        }, 160)
      }
    }

    if (!isAdminRoute(to) && to.query?.loginredirect !== null) {
      ReturnUrl.set(window.location.href);
    }

    resolveProjectAfterEachIfExists(store, to)

    if (isAdminRoute(to) || !isRequiredAuthRoute(to)) {
      clearTimeout(loginTimeoutId)
    } else {
      loginTimeoutId = setTimeout(() => {
        checkLogin(store)
      }, 15000)
    }
  })

  // eslint-disable-next-line sonarjs/cognitive-complexity
  router.beforeEach(async (to, from, next) => {
    if (!isAdminRoute(to) && isRequiredAuthRoute(to)) {
      clearTimeout(loginTimeoutId)

      let lastCheckLogin: string | null = null

      if (sessionStorage) {
        lastCheckLogin = sessionStorage.getItem('lastCheckLogin')
      }

      const isLastCheckOlderThenMinute = lastCheckLogin && Date.now() - Number.parseInt(lastCheckLogin, 10) > 60000

      if (!loginTimeoutId || isEmpty(store.state.AdminTemplate.user) || !lastCheckLogin || isLastCheckOlderThenMinute) {
        const isLogged = await checkLogin(store)

        if (isLogged) {
          await onLoginCheckSuccess(store, to, from, next)
        } else {
          if (!isAdminRoute(to) && !isAdminRoute(from)) {
            ReturnUrl.set(ReturnUrl.buildFullLink(to.fullPath));
          }

          next()
        }
      } else {
        await onLoginCheckSuccess(store, to, from, next)
      }
    } else {
      next()
    }

    if (i18n && !i18nHelper.defaultLocaleSet) {
      i18nHelper.initLocale(i18n, store)
    }
  })

  router.onError(error => {
    if(/loading chunk chunk-[a-zA-Z0-9]+ failed./i.test(error.message)) {
      window.location.reload();
    }
  });

}
